import { runInAction } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { useDrop } from "react-dnd";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Box from 'react-raster';
import {
  Avatar, AvatarUpload, Button, ConfirmationDialog,
  Icon, Input,
  Modal, Spacer, Text, Toggle
} from "src/components";
import { theme } from "src/config";
import { User } from "src/models";
import { Column, Row } from "src/pages/room/editData/gridElements";
import { AuthStore, UserStore } from "src/stores";
import { DataType } from "src/types";
import { useClickOutside } from "src/util/util";
import styled from "styled-components";
import { Card } from "./card";

export interface IGroupCardProps {
  data:       User;
  space_id:   number;
  userStore?: UserStore;
  authStore?: AuthStore;
  onClick?(): void;
}


export const GroupCard = inject("userStore", "authStore")(observer((props: IGroupCardProps): JSX.Element => {
    const { data, authStore, userStore, space_id } = props;
    const [showConfirmDialog,    setShowConfirmDialog]    = React.useState(false);
    const [showGroupAvatarInput, setShowGroupAvatarInput] = React.useState(false);
    const { getCurUser: currentUser } = authStore;
    const { t, i18n } = useTranslation();

    const cardRef = React.useRef<HTMLDivElement>(null);

    const [{ isOver }, drop] = useDrop(() => ({
      accept: DataType.User,
      drop: (d: { type: DataType; item: User }) => {
        data.addGroupUser(d.item.id);
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }));

    useClickOutside(cardRef, () => {
      if(userStore.getActive && data.id === userStore.getActive.id && !showConfirmDialog) {
        userStore.setActive(null, true);
      }
    });
    
    return (
      <GroupCardView
        isOver = {isOver}
        ref    = {(el) => {cardRef.current = el; drop(el);}}
        body   = {
          <GroupContainerHead>
            <Avatar size="medium" image={data.getAvatar} initials={data.getInitials} />
            <Text size="default">
              {data.getFullName}
              <br />
              <GroupMembersCount>
                {t("members")}: {data.getGroupUsers.length}
              </GroupMembersCount>
            </Text>
          </GroupContainerHead>
        }
        backgroundColor = {theme.color.white}
        expandedBody    = {
          <>
            {!showGroupAvatarInput && (
              <CardContainer>
                <GroupContainerHead>
                  <Box position="relative">
                  <Avatar
                    size     = "xlarge"
                    image    = {data.getAvatar}
                    initials = {data.getInitials}
                    onClick  = {() =>
                      setShowGroupAvatarInput(!showGroupAvatarInput)
                    }
                  />
                      <EditButton variant="icon">
                      <Icon
                        size    = "xxsmall"
                        color   = {theme.color.white}
                        iconId  = "edit"
                        onClick = {() =>
                          setShowGroupAvatarInput(!showGroupAvatarInput)
                        }
                      />
                    </EditButton>
                  </Box>
                  <Input
                    value    = {data.getFullName}
                    onBlur   = {() => data.save()}
                    label    = {t("name")}
                    onChange = {(val) => data.set("first_name", val, false)}
                    editOnFocus
                    autoFocus
                  />
                </GroupContainerHead>

                <GroupSettingsContent>
                  <Input
                    type     = "text"
                    value    = {data.getInitialsBySpace(space_id) || ""}
                    onBlur   = {() => data.save()}
                    label    = {t("initials")}
                    maxLength = {Number("3")}
                    onChange = {(val) =>
                      runInAction(() => (data.setSpaceSetting(space_id, "initials", val, true)))
                    }
                  />
                  <Toggle
                    label   = {t("hide_in_matrix")}
                    value   = {data.getCurSpaceSettings.hide_in_matrix || ""}
                    checked = {data.getCurSpaceSettings.hide_in_matrix}
                    onClick = {() => 
                      data.setCurSpaceSetting("hide_in_matrix", !data.getCurSpaceSettings.hide_in_matrix, true)
                    }
                  />
                  <Toggle
                    label   = {t("hide_in_list")}
                    value   = {data.getCurSpaceSettings.hide_in_list || ""}
                    checked = {data.getCurSpaceSettings.hide_in_list}
                    onClick = {() => 
                      data.setCurSpaceSetting("hide_in_list", !data.getCurSpaceSettings.hide_in_list, true)
                    }
                  />
                </GroupSettingsContent>

                <GroupCardContent>
                  <Column>
                    <StyledRow>
                      <Text size="small" weight="bold">{t("members")}:</Text>
                      <Spacer size="xxsmall" />
                      <Text size="small">{data.getGroupUsers.length} st</Text>
                    </StyledRow>
                    {data.created_by && (
                      <StyledRow>
                        <Text size="small" weight="bold">{t("created_by")}:</Text>
                        <Spacer size="xxsmall" />
                        <Text size="small">
                          {data.created_by
                            ? props.userStore.getItemByID(data.created_by)?.displayName
                            : null}
                        </Text>
                      </StyledRow>
                    )}
                    <StyledRow>
                      <Text size="small" weight="bold">{t("updated_by")}:</Text>
                      <Spacer size="xxsmall" />
                      <Text size="small">
                        {data.updated_by
                          ? props.userStore.getItemByID(data.updated_by)?.displayName
                          : null}
                      </Text>
                    </StyledRow>
                  </Column>
                  {props.userStore.getItems.length > 0 && (
                    <Column>
                      <Text size="small" weight="bold">{t("members")}:</Text>
                      <Spacer size="xxsmall" />
                      {data.getGroupUsers.map((user_id) => {
                        const user = props.userStore.getItemByID(user_id);
                        if (!user || !user.id) return null;

                        return (
                          <MemberRow key={user.id}>
                            <Avatar
                              size     = "default"
                              image    = {user.getAvatar}
                              initials = {user.getInitials}
                            />
                            <Spacer size="xxsmall" />
                            <Text style={{marginRight: "0.5rem"}} size="small">{`${user.displayName}${
                              user.company_name ? `, ${user.company_name}` : ``
                            }`}</Text>
                            <Button variant="icon">
                              <Icon
                                size="xsmall"
                                iconId="close"
                                color={theme.color.black}
                                onClick={() => {
                                  data.removeGroupUser(user.id);
                                  toast.success(t("user_removed_from_group"));
                                }}
                              />
                            </Button>
                          </MemberRow>
                        );
                      })}
                    {data.getGroupUsers.length < 1 && (
                      <MemberRow>{t('no_group_users')}</MemberRow>
                    )}
                    </Column>
                  )}
                </GroupCardContent>

                <ButtonContainer>
                  <Modal show={showConfirmDialog}>
                    <ConfirmationDialog
                      onConfirm={() => {
                        data.remove();
                        userStore.setActive(null);
                        toast.success(t("group_deleted"), {
                          icon: <Icon size="medium" iconId="delete"/>
                        });
                      }}
                      onCancel = {() => setShowConfirmDialog(false)}
                      label    = {t("confirm_action")}
                      message  = {t("confirm_action_delete_group", {item: data.getFullName})}
                    />
                  </Modal>
                  {currentUser.canEdit && (
                    <Button
                      variant = "icon"
                      size    = 'small'
                      onClick = {() => setShowConfirmDialog(true)}
                    >
                      <Icon iconId="delete" size="large" color={theme.color.grayDark} />
                    </Button>
                  )}
                  {/* tmp hide unlink groups since they are per space */}
                  {(currentUser.canEdit && false)&& (
                    <Button
                      variant = "icon"
                      onClick = {async () => {
                        userStore.setActive(null);
                        try {
                          await data.unlinkSpace(space_id);
                          toast.success(t("unlinked_group"));
                        } catch (err) {
                          console.error("Failed to unlink group", err);
                          toast.error(t("unlink_group_failed"));
                        }
                      }}
                    >
                      <Icon  size="large" iconId="unlink" color={theme.color.grayDark} />
                    </Button>
                  )}
                </ButtonContainer>
              </CardContainer>
            )}

            {showGroupAvatarInput && (
              <CardContainer>
                <GroupSettingsContent>
                  <Text size="small" >{t("edit_group_avatar")}</Text>
                  <AvatarUpload
                    onSave={(uri, size, mime) => {
                      data.setAvatar(uri, size, mime);
                      setShowGroupAvatarInput(false)
                    }
                    }
                  />
                </GroupSettingsContent>

                <ButtonContainer>
                  <Icon
                    iconId="close"
                    onClick={() => setShowGroupAvatarInput(false)}
                  />
                </ButtonContainer>
              </CardContainer>
            )}
          </>
        }
        key      = {data.id}
        editable = {currentUser.canEdit}
        height   = "auto"
        width    = "30rem"
        onClick  = {props.onClick || ((e) => userStore.setActive(data))}
        expanded = {Boolean(
          userStore.getActive && data.id === userStore.getActive.id
        )}
        footer = {
          <Footer>
            <Icon iconId="group" color={theme.color.grayDark} size="medium"/>
          </Footer>
        }
      />
    );
  })
);



const Footer = styled.div`
  display:         flex;
  flex-direction:  row-reverse;
  width:           100%;
  align-items:     center;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  display:        flex;
  width:          100%;
  flex-direction: row-reverse;
`;

const GroupContainerHead = styled.div`
  flex-direction: row;
  align-items:    center;
  display:        flex;
  gap:            1rem;
  font-size:      ${theme.font.size.default};
`;

const CardContainer = styled.div`
  display:        flex;
  flex-direction: column;
  gap:            1rem;
`;

const GroupSettingsContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
`;

const GroupCardContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
`;

const GroupMembersCount = styled.span`
  font-size: ${theme.font.size.small};
  color:     ${theme.color.grayDefault};
`;

const MemberRow = styled(Row)`
  align-items:   center;
  margin-bottom: 1rem;
  font-size:     ${theme.font.size.small};
`;

const StyledRow = styled(Row)`
  align-items: baseline;
`;

const EditButton = styled(Button)`
  top:              3.5rem;
  left:             4rem;
  position:         absolute;
  background-color: ${theme.color.buttons.primary.background};
  border:           1px solid ${theme.color.white};
  box-shadow:       0.5px 0.85px 2px 0px rgb(45 54 65 / 35%);

  :hover {
    background-color: ${theme.color.buttons.primary.hover};
  }
`;

const GroupCardView = styled(Card)``;